/**
 * Workspace shell — root component for the authed routes. Renders the
 * Sidebar, Header, and the workspace tree (or mobile-flattened view).
 *
 * Also responsible for URL ↔ store sync so deep links work.
 */
import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { resolvePathToView, getViewPath } from '@/lib/workspace/registry'
import { findPanel } from '@/stores/workspaceStore'
import { cn } from '@/lib/utils'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Sidebar } from '@/components/layout/Sidebar'
import { Header } from '@/components/layout/Header'
import { WorkspaceGrid } from './WorkspaceGrid'
import { MobilePanel } from './MobilePanel'
import { useIsMobile } from '@/hooks/useIsMobile'

export function WorkspaceLayout() {
  const isMobile = useIsMobile()
  const sidebarCollapsed = useSettingsStore((s) => s.sidebarCollapsed)
  const location = useLocation()
  const navigate = useNavigate()

  const hydrated = useWorkspaceStore((s) => s.hydrated)
  const rootNode = useWorkspaceStore((s) => s.rootNode)
  const focusedPanelId = useWorkspaceStore((s) => s.focusedPanelId)
  const navigateToView = useWorkspaceStore((s) => s.navigateToView)

  const isSyncing = useRef(false)

  // The focused tab's canonical address, params included — views that declare
  // `urlParams` (Options Chain, Charts) put what they're looking at in the URL,
  // so a deep link or a pop-out reopens the same thing. Both effects below
  // compare against path+search; comparing pathname alone would let the two
  // sides disagree about the query string and ping-pong.
  const focusedPath = useMemo(() => {
    const panel = findPanel(rootNode, focusedPanelId)
    if (!panel) return '/'
    const active = panel.views.find((v) => v.instanceId === panel.activeViewInstanceId)
    return active ? getViewPath(active.viewId, active.params) : '/'
  }, [rootNode, focusedPanelId])

  const currentPath = location.pathname + location.search

  // URL → Store
  useEffect(() => {
    if (!hydrated) return
    if (isSyncing.current) { isSyncing.current = false; return }
    if (currentPath === focusedPath) return
    const resolved = resolvePathToView(location.pathname, location.search)
    if (resolved) {
      navigateToView(resolved.viewId, resolved.params)
    } else {
      // Unknown path (e.g. a stale `/dashboard` link or a deep AppShell route
      // that doesn't map to a workspace view). Redirect to the root view rather
      // than letting the store→URL effect below silently rewrite the URL back to
      // the last focused view — that swallows the navigation the user asked for.
      navigate('/', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, hydrated])

  // Store → URL
  useEffect(() => {
    if (!hydrated) return
    if (focusedPath === currentPath) return
    isSyncing.current = true
    navigate(focusedPath, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedPath, hydrated])

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full bg-background text-foreground">
        {!isMobile && <Sidebar />}
        <div
          className={cn(
            'flex flex-1 min-w-0 flex-col transition-all duration-200',
            !isMobile && (sidebarCollapsed ? 'ml-16' : 'ml-56'),
          )}
        >
          <Header isMobile={isMobile} />
          <main className="flex-1 min-h-0">
            {isMobile ? <MobilePanel node={rootNode} /> : <WorkspaceGrid node={rootNode} />}
          </main>
        </div>
      </div>
    </TooltipProvider>
  )
}
